import { KeyboardEvent, useEffect, useId, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRefundAccount } from '../Home/Component/RefundAccountFields';

export default function BillRefundAccount({ refreshKey, onEdit }: { refreshKey: number; onEdit: () => void }) {
  const account = useRefundAccount();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuWrapper = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menuItem = useRef<HTMLButtonElement>(null);
  const id = useId();
  useEffect(() => { if (refreshKey > 0) void account.load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!menuOpen) return;
    menuItem.current?.focus();
    const outside = (event: PointerEvent) => {
      if (!menuWrapper.current?.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [menuOpen]);
  const edit = () => { setMenuOpen(false); trigger.current?.focus(); onEdit(); };
  const keyboard = (event: KeyboardEvent) => {
    if (event.key === 'Escape' && menuOpen) {
      event.preventDefault(); event.stopPropagation(); setMenuOpen(false); trigger.current?.focus();
    } else if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
      event.preventDefault(); setMenuOpen(true); menuItem.current?.focus();
    }
  };
  return <Section aria-label="내 환불 계좌">
    <header><h3>내 환불 계좌</h3>
      {account.ready && !!account.value.version && <MenuWrapper ref={menuWrapper} onKeyDown={keyboard}
        onBlur={event => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setMenuOpen(false); }}>
        <MenuTrigger ref={trigger} type="button" aria-label="환불 계좌 더보기" aria-haspopup="menu" aria-expanded={menuOpen}
          aria-controls={menuOpen ? id : undefined} onClick={() => setMenuOpen(value => !value)}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><circle cx="12" cy="5" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="12" cy="19" r="1.8"/></svg>
        </MenuTrigger>
        {menuOpen && <Menu role="menu" aria-label="환불 계좌 메뉴" id={id}>
          <button ref={menuItem} type="button" role="menuitem" tabIndex={-1} onClick={edit}>수정</button>
        </Menu>}
      </MenuWrapper>}
    </header>
    {!account.ready && !account.error && <p role="status">환불 계좌를 불러오는 중입니다.</p>}
    {account.error && <><p role="alert">{account.error}</p><button className="account-action" type="button" onClick={() => void account.load()}>다시 조회</button></>}
    {account.ready && (account.value.version ? <>
      <dl><div><dt>은행</dt><dd>{account.value.bank}</dd></div><div><dt>계좌번호</dt><dd>{account.value.account}</dd></div><div><dt>예금주</dt><dd>{account.value.holder}</dd></div></dl>
    </> : <><p>등록된 환불 계좌가 없습니다.</p><button className="account-action" type="button" onClick={edit}>환불 계좌 등록하기</button></>)}
  </Section>;
}
const Section = styled.section`
  margin: 24px 0 4px;
  padding: 20px;
  border: 1px solid var(--user-border);
  border-radius: 12px;
  background: var(--user-surface-raised);
  color: var(--user-text);
  min-width: 0;
  > header { display: flex; align-items: center; justify-content: space-between; gap: 12px; min-height: 24px; }
  h3 { margin: 0; font-size: 16px; font-weight: 700; line-height: 1.5; }
  && p { margin: 8px 0 0; font-size: 14px; line-height: 1.6; color: var(--user-text-muted); }
  dl { margin: 16px 0 0; font-size: 14px; line-height: 1.6; }
  dl div { display: flex; justify-content: space-between; gap: 16px; margin-top: 8px; }
  dt { flex: 0 0 auto; color: var(--user-text-muted); }
  dd { margin: 0; min-width: 0; text-align: right; overflow-wrap: anywhere; font-weight: 600; font-variant-numeric: tabular-nums; user-select: text; }
  .account-action {
    display: block; width: 100%; min-height: 44px; margin-top: 20px; padding: 11px 16px;
    border: 1px solid var(--user-accent); border-radius: 8px;
    background: transparent; color: var(--user-accent); font: inherit; font-size: 14px; font-weight: 600;
    line-height: 1.5; cursor: pointer; touch-action: manipulation;
  }
  .account-action:hover { background: var(--user-accent-soft); }
  .account-action:active { background: var(--user-surface); }
  button:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 3px; }
  && [role="alert"] { color: var(--user-error); }
  @media (max-width: 360px) { padding: 16px; }
`;
const MenuWrapper = styled.div`position:relative;margin:-10px -10px -10px 0;flex-shrink:0;z-index:2;`;
const MenuTrigger = styled.button`
  display:grid;place-items:center;width:44px;height:44px;padding:0;border:0;border-radius:6px;
  background:transparent;color:var(--user-text-muted);cursor:pointer;
  &:hover,&[aria-expanded="true"]{background:var(--user-accent-soft);color:var(--user-text);}
`;
const Menu = styled.div`
  position:absolute;top:calc(100% + 4px);right:0;min-width:128px;padding:4px;border:1px solid var(--user-border);border-radius:8px;
  background:var(--user-surface-raised);color:var(--user-text);
  > button{display:block;width:100%;min-height:44px;padding:10px 12px;border:0;border-radius:5px;background:transparent;color:inherit;font:inherit;font-size:14px;text-align:left;cursor:pointer;}
  > button:hover{background:var(--user-accent-soft);}
`;
